import { motion } from "framer-motion";
import { Mail, Heart, MapPin, Calendar, ArrowUpRight, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import winnersCupLogo from "@/assets/winners-cup-logo-red.png";
import AddToCalendarDialog, { EVENT_2027_DETAILS } from "@/components/AddToCalendarDialog";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const quickLinks = [
    { name: "About the Event", href: "/event" },
    { name: "Venue", href: "/venue" },
    { name: "Hotel", href: "/hotel" },
    { name: "Travel", href: "/travel" },
    { name: "Vendors", href: "/vendors" },
    { name: "2027 Info", href: "/pre-register" },
  ];

  return (
    <footer className="bg-dark text-white relative overflow-hidden border-t border-white/10">
      {/* Gradient accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-32 bg-primary/3 blur-[80px] rounded-full" />

      <div className="container mx-auto px-6 py-20 relative z-10">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" onClick={scrollToTop} className="inline-block group">
              <motion.div className="flex items-center gap-3" whileHover={{ scale: 1.03 }}>
                <img src={winnersCupLogo} alt="Winner's Cup" className="h-14 w-auto" />
                <span className="font-display text-3xl text-gradient">2027</span>
              </motion.div>
            </Link>
            <p className="mt-6 text-white/60 max-w-md leading-relaxed text-sm">
              The premier West Coast gymnastics competition. February 26–28, 2027 at Ontario Convention Center, California.
            </p>
            
            <AddToCalendarDialog eventDetails={EVENT_2027_DETAILS}>
              <motion.button className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2.5 border border-white/15 bg-white/5 hover:bg-white/10 group transition-colors" whileHover={{ scale: 1.02 }}>
                <Calendar className="w-4 h-4 text-primary" />
                <span className="text-white/80 text-sm group-hover:text-primary transition-colors">Feb 26–28, 2027</span>
                <span className="text-white/50 text-xs">+ Add</span>
              </motion.button>
            </AddToCalendarDialog>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg text-white mb-6 flex items-center gap-2">
              <div className="w-8 h-0.5 bg-gradient-coral rounded-full" />
              LINKS
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} onClick={scrollToTop} className="text-white/65 hover:text-primary transition-colors text-sm flex items-center gap-1 group">
                    {item.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-lg text-white mb-6 flex items-center gap-2">
              <div className="w-8 h-0.5 bg-gradient-coral rounded-full" />
              CONTACT
            </h4>
            <div className="space-y-4">
              <a href="mailto:meets@winnersgymnastics.com" className="flex items-center gap-3 text-white/70 hover:text-primary transition-colors group">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                  <Mail className="w-4 h-4 text-primary" />
                </div>
                <span className="text-xs">meets@winnersgymnastics.com</span>
              </a>
              <a href="tel:+18889770042" className="flex items-start gap-3 text-white/70 hover:text-primary transition-colors group">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/10 transition-colors">
                  <Phone className="w-4 h-4 text-primary" />
                </div>
                <span className="text-xs leading-relaxed pt-1">
                  Event Questions?<br />
                  Call Meet Operations Group<br />
                  <span className="text-white/90 group-hover:text-primary">888-977-0042</span>
                </span>
              </a>
              <div className="flex items-start gap-3 text-white/70">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-primary" />
                </div>
                <span className="text-xs leading-relaxed pt-2.5">Ontario Convention Center<br />Ontario, California</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-white/70 text-xs flex items-center gap-1.5 justify-center md:justify-start">
                Hosted with <Heart className="w-3 h-3 text-primary fill-primary" /> by Winner's Gymnastics
              </p>
              <p className="text-white/50 text-xs mt-1">Event Powered by Meet Operations Group</p>
            </div>
            <p className="text-white/50 text-xs">© {currentYear} Winner's Gymnastics</p>
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      <motion.button
        onClick={scrollToTop}
        className="absolute bottom-8 right-6 w-12 h-12 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 flex items-center justify-center text-primary transition-colors"
        whileHover={{ y: -3 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Scroll to top"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </motion.button>
    </footer>
  );
};

export default Footer;
